import { Link } from "@tanstack/react-router";
import { UserRound } from "lucide-react";
import { useState } from "react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { useUserProfile } from "../profile/use-user-profile";
import {
  dismissProfilePrompt,
  joinFields,
  missingProfileFields,
  profilePromptDismissed,
} from "./profile-prompt";

/**
 * A card on the Overview that asks an attendee to finish their profile: a
 * photo, a job title and a company are what the other attendees see. It is a
 * card and not a second dialog, so it never stacks on the signals picker.
 *
 * This is the gate. The page decides when `show` is true (an attendee row, the
 * signals dialog closed and not about to open), and a person who said "Not now"
 * on this device is never asked again. Only when the card could show does the
 * inner component mount and read the profile, so a member who dismissed it or
 * has nothing to add costs no read on an Overview visit.
 *
 * `onDismissed` runs after "Not now", when the focused button is gone: the
 * page puts focus somewhere sensible instead of letting it fall to the body.
 */
export function ProfilePromptCard({
  uid,
  show,
  attendeeList,
  onDismissed,
}: {
  uid: string;
  show: boolean;
  /** Whether the event shows an attendee list, which the copy may mention. */
  attendeeList: boolean;
  onDismissed?: () => void;
}) {
  const [dismissed, setDismissed] = useState(() => profilePromptDismissed(uid));
  if (!show || dismissed) return null;
  return (
    <LoadedProfilePrompt
      uid={uid}
      attendeeList={attendeeList}
      onDismiss={() => {
        dismissProfilePrompt(uid);
        setDismissed(true);
        onDismissed?.();
      }}
    />
  );
}

/**
 * Nothing is drawn, and so nothing in the layout moves, until the profile has
 * loaded and lacks something; it does not show while loading, when it cannot be
 * read, or when it has all three fields.
 */
function LoadedProfilePrompt({
  uid,
  attendeeList,
  onDismiss,
}: {
  uid: string;
  attendeeList: boolean;
  onDismiss: () => void;
}) {
  const profile = useUserProfile(uid);
  if (profile.state.status !== "success") return null;
  const missing = missingProfileFields(profile.state.profile);
  if (missing.length === 0) return null;

  return (
    <Card data-testid="profile-prompt">
      <CardContent className="flex flex-col gap-4 py-5">
        <div className="flex items-start gap-3">
          <UserRound
            className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <div className="min-w-0 space-y-1">
            <h2 className="text-lg font-semibold text-foreground">
              Complete your profile
            </h2>
            <p
              className="text-base text-muted-foreground wrap-anywhere"
              data-testid="profile-prompt-missing"
            >
              {attendeeList
                ? `Add your ${joinFields(missing)} so other attendees know who you are. They show next to your name in the attendee list.`
                : `Add your ${joinFields(missing)} so the people you meet at the event know who you are.`}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            asChild
            variant="cta"
            className="h-11 w-full sm:w-auto"
            data-testid="profile-prompt-go"
          >
            <Link to="/profile">Go to my profile</Link>
          </Button>
          <Button
            variant="ghost"
            className="h-11 w-full sm:w-auto"
            onClick={onDismiss}
            data-testid="profile-prompt-dismiss"
          >
            Not now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
